import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Paper, TextField, Button, Typography, Container, Alert, CircularProgress } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import apiClient from '../../services/apiClient';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  if (!token) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="error" sx={{ mb: 2 }}>Invalid or missing reset token.</Alert>
            <Button component={Link} to="/forgot-password">Request a new reset link</Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      await apiClient.resetPassword(token, newPassword);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.message || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8 }}>
        <Paper sx={{ p: 4 }}>
          {success ? (
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircle color="success" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Password Reset!</Typography>
              <Typography color="text.secondary">Your password has been updated. Redirecting to login...</Typography>
            </Box>
          ) : (
            <>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Set New Password</Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>Enter your new password below.</Typography>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth label="New Password" type="password"
                  value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  margin="normal" required helperText="Minimum 8 characters"
                />
                <TextField
                  fullWidth label="Confirm New Password" type="password"
                  value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  margin="normal" required
                />
                <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}>
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPassword;
